import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import AddNumbersTab from 'views/PrepaidLoyalty/commons/tabs/AddNumbersTab';
import NameLoyaltyTab from 'views/PrepaidLoyalty/commons/tabs/NameLoyaltyTab';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/registerNewLoyaltyStyle';
import { useHistory } from 'react-router';
import { Caption, HeadingMBold } from 'components/Typography/Typography';
import { BillsService } from 'services/bills/bills';
import { finalize } from 'rxjs/operators';
import { dispatch } from 'redux/store';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { BILLING_TYPE, OFFER_DIRECT_MESSAGE_NAME } from '../commons/constants';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import Note from 'views/Numbers/Details/Services/subcomponents/common/Note';
import { ContactService } from 'services/contact/contact';
import { useSelector } from 'react-redux';
import { contactNumberSelector, emailSelector, upIdSelector, userNameSelector } from 'redux/modules/session/selectors';
import { getLanguage } from 'utils/language/language';
import { AccountsService } from 'services/accounts/accounts';
import { AccountDetails } from 'utils/api/models/accounts/accountDetails';
import { DepartmentsService } from 'services/departments/departments';
import { SingleAccountManager } from 'utils/api/models/users/singleAccountManager';
import CustomButton from 'components/CustomButtons/Button';

// @ts-ignore
const useStyles = makeStyles(styles);

const steps = {
	ADD_NUMBERS: 0,
	NAME_LOYALTY: 1,
};

interface IRegisteringNewLoyalty {
	addGroup: (name: string, numbers: string[]) => void;
	loading: boolean;
	setLoading: (isLoading: boolean) => void;
}

const RegisteringNewLoyalty = ({ addGroup, loading, setLoading }: IRegisteringNewLoyalty) => {
	const [activeTab, setActiveTab] = useState(steps.ADD_NUMBERS);
	const [numbers, setNumbers] = useState<string[]>([]);
	const [name, setName] = useState<string>('');
	const [isEnterpiseActive, setIsEnterpriseActive] = useState<boolean>(true);
	const [documentId, setDocumentId] = useState<string | null>(null);
	const [managers, setManagers] = useState<SingleAccountManager[]>([]);
	const [disabledEmailSent, setDisabledEmailSent] = useState<boolean>(false);

	const upId: number = useSelector(upIdSelector);
	const username: string = useSelector(userNameSelector);
	const userEmail: string = useSelector(emailSelector);
	const userPhone: string = useSelector(contactNumberSelector);

	const classes = useStyles();
	const history = useHistory();
	const lang = getLanguage();

	useEffect(() => {
		dispatch(showLoadingIndicator);
		isEnterpiseLoyaltyAccountActive();
		corpAccountDetails();
	}, []);

	const isEnterpiseLoyaltyAccountActive = () => {
		BillsService.getCorpBillingIds()
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator);
				})
			)
			.subscribe((response) => {
				setIsEnterpriseActive(
					response.billingIds.some((bi) => bi.addBillingType === BILLING_TYPE.ENTERPRISE_LOYALTY)
				);
			});
	};

	const corpAccountDetails = () => {
		AccountsService.getCorpAccountDetails(upId.toString()).subscribe((account: AccountDetails) => {
			if (account) {
				const documentId = account?.accountDetails?.documentId;
				const documentType = account?.accountDetails?.documentType;
				setDocumentId(documentId);
				DepartmentsService.getAccountManagerDetails(documentId, documentType)
					.pipe(finalize(() => dispatch(hideLoadingIndicator())))
					.subscribe(({ managers }) => {
						setManagers(managers);
					});
			} else {
				dispatch(hideLoadingIndicator());
			}
		});
	};

	const addNumbers = (newNumbers: string[]) => {
		setNumbers([...new Set([...numbers, ...newNumbers].flat())]);
	};

	const removeNumbers = (delNumbers: string[]) => {
		setNumbers((numb) => numb.filter((n) => delNumbers.indexOf(n) === -1));
	};

	const goToPrepaidLoyalty = useCallback(() => {
		history.push('/dashboard/gift-program');
	}, [history]);

	const nextStepDisabled = useMemo(() => {
		switch (activeTab) {
			case steps.ADD_NUMBERS:
				return numbers.length === 0;
			case steps.NAME_LOYALTY:
				return name.length === 0;
		}
	}, [activeTab, numbers, name]);

	const nextStepAction = useCallback(() => {
		if (activeTab === steps.ADD_NUMBERS) {
			setActiveTab(steps.NAME_LOYALTY);
		} else if (activeTab === steps.NAME_LOYALTY) {
			setLoading(true);
			addGroup(name, numbers);
		}
	}, [activeTab, name, numbers, setActiveTab, setLoading, addGroup]);

	const backCancelAction = useCallback(() => {
		if (activeTab === steps.ADD_NUMBERS) {
			goToPrepaidLoyalty();
		} else {
			setActiveTab((value) => value - 1);
		}
	}, [activeTab, goToPrepaidLoyalty]);

	const sendEmail = () => {
		dispatch(showLoadingIndicator());
		setDisabledEmailSent(true);
		(managers || []).forEach((manager, i) => sendEmailToManager(manager?.emailAddress, managers?.length - 1 === i));
	};

	const sendEmailToManager = (emailAddress: string, message: boolean) => {
		ContactService.sendDirectMessage({
			offer: OFFER_DIRECT_MESSAGE_NAME,
			subject: translate(
				'prepaid.loyalty.add.new.services.payment.method.credit.card.account.managers.subject.message'
			),
			phone: userPhone,
			email: emailAddress,
			language: lang,
			message: JSON.stringify({
				username: username,
				documentId: documentId,
				email: userEmail,
			}),
			saveContact: true,
			returnReference: true,
			sendEmailToSender: true,
			sendCopyToSender: false,
			sendSms: false,
		})
			.pipe(
				finalize(() => {
					if (message) {
						dispatch(hideLoadingIndicator());
					}
				})
			)
			.subscribe(() => {});
	};

	const buttons: types.prepaidLoyalty.IButton[] = useMemo(
		() => [
			{
				color: 'tertiary-light',
				text: activeTab === steps.ADD_NUMBERS ? translate('cancel') : translate('back'),
				onClick: backCancelAction,
			},
			{
				color: 'primary-light',
				text: translate(activeTab === steps.NAME_LOYALTY ? 'add' : 'next'),
				onClick: nextStepAction,
				disabled: nextStepDisabled,
			},
		],
		[activeTab, nextStepDisabled, nextStepAction, backCancelAction]
	);

	const ErrorMessage = (
		<div className={classes.noteWrapper}>
			<Note text={translate('prepaid.newLoyalty.monthlyBillIsBlocked')} />
		</div>
	);

	const SendToManager = (
		<>
			<Note text={translate('prepaid.newLoyalty.monthlyBillIsBlocked')} />
			<div>
				<CustomButton color="tertiary-light" disabled={disabledEmailSent} onClick={sendEmail}>
					{translate('prepaid.loyalty.add.new.services.payment.method.credit.card.account.managers.send')}
				</CustomButton>
			</div>
		</>
	);

	return (
		<div className={classes.registerNewLoyaltyWrapper}>
			<div className={classes.headingWrapper}>
				<HeadingMBold color="secondary-light" disableMargin>
					{translate('path.prepaid.loyalty')}
				</HeadingMBold>
			</div>
			<ConditionalRender
				show={!isEnterpiseActive && activeTab !== steps.NAME_LOYALTY && managers.length > 0}
				onTrue={() => SendToManager}
			></ConditionalRender>
			<ConditionalRender
				show={!isEnterpiseActive && activeTab !== steps.NAME_LOYALTY && managers.length === 0}
				onTrue={() => ErrorMessage}
			></ConditionalRender>
			<TabsWithDots
				loading={loading}
				tabs={[
					{
						name: translate('prepaid.loyalty.add.numbers'),
						content: (
							<AddNumbersTab
								numbers={numbers}
								addNumbers={addNumbers}
								removeNumbers={removeNumbers}
								buttons={buttons}
							/>
						),
					},
					{
						name: translate('prepaid.loyalty.name.loyalty'),
						content: <NameLoyaltyTab name={name} setName={setName} buttons={buttons} />,
					},
				]}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
			/>
		</div>
	);
};

export default RegisteringNewLoyalty;
